import apiClient from "./api";
import { AxiosError } from "axios";

// ============= TIPOS DA API =============

export interface ApiOrcamentoResponse {
  id: string;
  token: string;
  codigoSequencial: number;
  fornecedor: {
    id: string;
    nome: string;
    cnpj: string;
    razaoSocial: string;
    endereco?: string;
    telefone?: string;
    email?: string;
    cep?: string;
    cnaes: {
      id: string;
      codigo: string;
      descricao: string;
      principal: boolean;
    }[];
  };
  planilha: {
    id: string;
    evento: string;
    dataInicio?: string;
    dataFim?: string;
    localEvento?: string;
    ufEvento?: string;
    cliente: {
      id: string;
      nome: string;
    };
  };
  /** Produtor(es) que solicitaram os itens. Vazio em orçamentos antigos. */
  produtores?: {
    nome?: string | null;
    email: string;
    telefone?: string | null;
  }[];
  itens: {
    id: string;
    descricao: string;
    descritivo?: string;
    valorUnitario?: number;
    quantidade: number;
    diaria: number;
  }[];
  etapaAtual: number;
  status:
    | "aguardando"
    | "em_preenchimento"
    | "concluido"
    | "expirado"
    | "rejeitado";
  /** Verdadeiro quando a API reconheceu a edição interna. */
  edicaoInterna?: boolean;

  dadosIdentificacao?: {
    /** Primeiro da lista; é o que a API antiga devolvia. */
    cnaeId?: string;
    /** Ausente na API antiga, que só guardava um. */
    cnaeIds?: string[];
    responsavel: {
      nome: string;
      email: string;
      cpf: string;
      rg: string;
      cargo: string;
    };
  };

  dadosValores?: {
    dataOrcamento: string;
    validadeDias: number;
    itens: Record<string, number>;
    totalGeral: number;
  };

  dadosAssinatura?: {
    tipo: "manual" | "upload";
    assinaturaData: string;
  };
}

export interface Etapa1Request {
  /**
   * Primeiro CNAE da lista. Vai junto porque a API anterior só lê este: se o
   * front for publicado antes dela, o orçamento ainda salva com um.
   */
  cnaeId: string;
  cnaeIds: string[];
  responsavel: {
    nome: string;
    email: string;
    cpf: string;
    rg: string;
    cargo: string;
  };
  /** Telefone da empresa informado pelo fornecedor. Obrigatório. */
  telefoneEmpresa?: string;
}

export interface Etapa2Request {
  dataOrcamento: string;
  validadeDias: number;
  itens: Record<string, number>;
  formaPagamento?: string;
  clausulaCancelamento?: string;
  observacoesProposta?: string;
}

export interface Etapa3Request {
  tipo: "manual" | "upload" | "digitada";
  assinaturaData: string;
}

export interface FinalizarRequest {
  termo1: boolean;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string>;
}

// ============= SERVICE =============

class OrcamentoFornecedorService {
  private readonly baseUrl = "/orcamentos";

  /**
   * Edição interna: a equipe corrigindo a cotação pelo mesmo portal do
   * fornecedor, em qualquer status. Toda chamada leva `?edicao=interna`, e a
   * API exige o token de login, que o apiClient já anexa para quem está logado.
   *
   * Fica no serviço, e não em parâmetro de cada método, porque o modo é da
   * página inteira: a página do portal define ao abrir e desfaz ao sair.
   */
  private edicaoInterna = false;

  definirEdicaoInterna(ativa: boolean) {
    this.edicaoInterna = ativa;
  }

  private get params() {
    return this.edicaoInterna ? { edicao: "interna" } : undefined;
  }

  /**
   * 1. Carregar dados do orçamento
   * GET /api/orcamentos/{token}
   */
  async carregarOrcamento(token: string): Promise<ApiOrcamentoResponse> {
    try {
      const response = await apiClient.get<ApiResponse<ApiOrcamentoResponse>>(
        `${this.baseUrl}/${token}`,
        { params: this.params },
      );
      // API retorna { success: true, data: {...} }
      return response.data.data!;
    } catch (error) {
      this.handleError(error, "Erro ao carregar orçamento");
      throw error;
    }
  }

  /**
   * 2. Salvar Etapa 1 - Identificação
   * PUT /api/orcamentos/{token}/etapa1
   */
  async salvarEtapa1(
    token: string,
    dados: Etapa1Request,
  ): Promise<ApiResponse> {
    try {
      // Formatar CPF removendo máscara para envio
      const dadosFormatados = {
        ...dados,
        responsavel: {
          ...dados.responsavel,
          cpf: this.removerMascara(dados.responsavel.cpf),
        },
      };

      const response = await apiClient.put<ApiResponse>(
        `${this.baseUrl}/${token}/etapa1`,
        dadosFormatados,
        { params: this.params },
      );
      return response.data;
    } catch (error) {
      this.handleError(error, "Erro ao salvar dados de identificação");
      throw error;
    }
  }

  /**
   * 3. Salvar Etapa 2 - Valores
   * PUT /api/orcamentos/{token}/etapa2
   */
  async salvarEtapa2(
    token: string,
    dados: Etapa2Request,
  ): Promise<ApiResponse<{ totalGeral: number; etapaAtual: number }>> {
    try {
      const response = await apiClient.put<
        ApiResponse<{ totalGeral: number; etapaAtual: number }>
      >(`${this.baseUrl}/${token}/etapa2`, dados, { params: this.params });
      return response.data;
    } catch (error) {
      this.handleError(error, "Erro ao salvar valores do orçamento");
      throw error;
    }
  }

  /**
   * 4. Salvar Etapa 3 - Assinatura
   * PUT /api/orcamentos/{token}/etapa3
   */
  async salvarEtapa3(
    token: string,
    dados: Etapa3Request,
  ): Promise<ApiResponse> {
    try {
      const response = await apiClient.put<ApiResponse>(
        `${this.baseUrl}/${token}/etapa3`,
        dados,
        { params: this.params },
      );
      return response.data;
    } catch (error) {
      this.handleError(error, "Erro ao salvar assinatura");
      throw error;
    }
  }

  /**
   * 5. Finalizar Orçamento
   * POST /api/orcamentos/{token}/finalizar
   */
  async finalizarOrcamento(
    token: string,
    dados: FinalizarRequest,
  ): Promise<
    ApiResponse<{ orcamentoId: string; status: string; pdfUrl?: string }>
  > {
    try {
      const response = await apiClient.post<
        ApiResponse<{ orcamentoId: string; status: string; pdfUrl?: string }>
      >(`${this.baseUrl}/${token}/finalizar`, dados, { params: this.params });
      return response.data;
    } catch (error) {
      this.handleError(error, "Erro ao finalizar orçamento");
      throw error;
    }
  }

  /**
   * 6. Upload de Logo (Opcional)
   * POST /api/orcamentos/{token}/logo
   */
  async uploadLogo(
    token: string,
    file: File,
  ): Promise<ApiResponse<{ logoUrl: string }>> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post<ApiResponse<{ logoUrl: string }>>(
        `${this.baseUrl}/${token}/logo`,
        formData,
        {
          params: this.params,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      this.handleError(error, "Erro ao fazer upload da logo");
      throw error;
    }
  }

  /**
   * 6.1. Rejeitar Orçamento
   * POST /api/orcamentos-fornecedor/{token}/rejeitar
   */
  async rejeitarOrcamento(
    token: string,
    dados: { justificativa: string },
  ): Promise<ApiResponse> {
    try {
      const response = await apiClient.post<ApiResponse>(
        `${this.baseUrl}/token/${token}/rejeitar`,
        dados,
      );
      return response.data;
    } catch (error) {
      this.handleError(error, "Erro ao rejeitar orçamento");
      throw error;
    }
  }

  /**
   * 6.1. Obter URL da logo salva
   * GET /api/orcamentos/{token}/logo
   */
  async getLogoUrl(token: string): Promise<string | null> {
    try {
      const response = await apiClient.get<ApiResponse<{ logoUrl: string }>>(
        `${this.baseUrl}/${token}/logo`,
      );
      return response.data.data?.logoUrl || null;
    } catch (error) {
      // Retornar null se não houver logo (404 é esperado)
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      console.error("Erro ao buscar logo:", error);
      return null;
    }
  }

  /**
   * 7. Upload de PDF do Orçamento
   * POST /api/orcamentos/{token}/pdf
   */
  async uploadPdf(
    token: string,
    file: File,
  ): Promise<ApiResponse<{ pdfUrl: string }>> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post<ApiResponse<{ pdfUrl: string }>>(
        `${this.baseUrl}/${token}/pdf`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      this.handleError(error, "Erro ao fazer upload do PDF");
      throw error;
    }
  }

  /**
   * Upload do PDF de rejeição gerado no frontend
   * POST /api/orcamentos/token/{token}/pdf-rejeicao
   */
  async uploadPdfRejeicao(
    token: string,
    pdfBase64: string,
  ): Promise<ApiResponse<{ pdfUrl: string }>> {
    try {
      const response = await apiClient.post<ApiResponse<{ pdfUrl: string }>>(
        `${this.baseUrl}/token/${token}/pdf-rejeicao`,
        { pdfBase64 },
      );
      return response.data;
    } catch (error) {
      this.handleError(error, "Erro ao fazer upload do PDF de rejeição");
      throw error;
    }
  }

  /**
   * 8. Gerar PDF a partir de HTML (conversão server-side)
   * POST /api/orcamentos/{token}/gerar-pdf
   */
  async gerarPdfFromHtml(
    token: string,
    pdfBase64: string,
  ): Promise<ApiResponse<{ pdfUrl: string }>> {
    try {
      const response = await apiClient.post<ApiResponse<{ pdfUrl: string }>>(
        `${this.baseUrl}/${token}/pdf`,
        { pdfBase64 },
        {
          params: this.params,
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      this.handleError(error, "Erro ao gerar PDF");
      throw error;
    }
  }

  // ============= HELPERS =============

  /**
   * Remove máscara de CPF/CNPJ
   */
  private removerMascara(valor: string): string {
    return valor.replace(/[^\d]/g, "");
  }

  /**
   * Tratamento de erros da API
   */
  private handleError(error: unknown, defaultMessage: string): void {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const data = error.response?.data;

      console.error("API Error:", {
        status,
        message: data?.message || error.message,
        errors: data?.errors,
      });

      // Erros específicos por status
      if (status === 404) {
        throw new Error("Token inválido ou orçamento não encontrado");
      } else if (status === 409) {
        throw new Error("Orçamento já foi finalizado e não pode ser editado");
      } else if (status === 400 && data?.errors) {
        // Validações do backend
        const errorMessages = Object.entries(data.errors)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join("\n");
        throw new Error(errorMessages);
      } else if (data?.message) {
        throw new Error(data.message);
      }
    }

    throw new Error(defaultMessage);
  }

  /**
   * Validação de arquivo de imagem
   */
  validateImageFile(file: File, maxSizeMB: number = 2): string | null {
    if (!file.type.startsWith("image/")) {
      return "Apenas arquivos de imagem são permitidos (PNG, JPG, JPEG)";
    }

    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      return `O arquivo deve ter no máximo ${maxSizeMB}MB`;
    }

    return null;
  }

  /**
   * Validação de arquivo PDF
   */
  validatePdfFile(file: File, maxSizeMB: number = 10): string | null {
    if (file.type !== "application/pdf") {
      return "Apenas arquivos PDF são permitidos";
    }

    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      return `O arquivo deve ter no máximo ${maxSizeMB}MB`;
    }

    return null;
  }
}

export const orcamentoFornecedorService = new OrcamentoFornecedorService();
